import api from "lib/axios";
import { ApiEnvelope, PaginatedResult, unwrapData, unwrapList } from "./apiTypes";

type Query = Record<string, unknown> | undefined;

/** Normalize audience-options payloads so UI never crashes on shape drift. */
function normalizeAudienceOptions(raw: AudienceOptions | null | undefined): AudienceOptions {
  const stages = raw?.academic?.stages ?? raw?.basic?.stages ?? [];
  const grades = raw?.academic?.grades ?? raw?.basic?.grades ?? [];
  return {
    audience_types: Array.isArray(raw?.audience_types) ? raw!.audience_types : [],
    target_types: Array.isArray(raw?.target_types) ? raw!.target_types : [],
    education_types: Array.isArray(raw?.education_types) ? raw!.education_types : [],
    academic: { stages, grades },
    // Keep legacy key populated so any old `options.basic.stages` access stays safe.
    basic: { stages, grades },
    courses: Array.isArray(raw?.courses) ? raw!.courses : [],
  };
}

export type NotificationTargetType =
  | "none"
  | "course"
  | "course_announcement"
  | "course_chat"
  | "course_bundle"
  | "url";

export type NotificationAudienceType =
  | "all_students"
  | "all_instructors"
  | "selected_students"
  | "selected_instructors"
  | "course_students"
  | "filtered_students"
  | "filtered_instructors";

export type AdminNotification = {
  id: number;
  title: string;
  body: string;
  type: string;
  target_type: NotificationTargetType | null;
  target_data: Record<string, unknown> | null;
  audience_type: NotificationAudienceType;
  audience_filters: Record<string, unknown> | null;
  status: string;
  recipients_count: number;
  sent_count: number;
  failed_count: number;
  scheduled_at: string | null;
  sent_at: string | null;
  created_at: string | null;
  recipients?: NotificationRecipient[];
};

export type NotificationRecipient = {
  id: number;
  recipient_id: number;
  recipient_type: string;
  recipient_name?: string | null;
  status: string;
  sent_at: string | null;
  read_at: string | null;
  error: string | null;
};

export type NamedOption = { id: number; name: string };

export type EducationType = "basic";

export type EducationTypeOption = {
  value: EducationType;
  label_ar: string;
  label_en: string;
};

export type GradeOption = NamedOption & { stage_id?: number | null };

export type AudienceOptions = {
  audience_types: string[];
  target_types: string[];
  /** @deprecated Prefer `academic` — kept optional for older API shapes. */
  education_types?: EducationTypeOption[];
  /** @deprecated Prefer `academic`. */
  basic?: {
    stages: NamedOption[];
    grades: GradeOption[];
  };
  academic?: {
    stages: NamedOption[];
    grades: GradeOption[];
  };
  courses: NamedOption[];
};

export type NotificationFilters = {
  course_id?: number | null;
  stage_id?: number | null;
  grade_id?: number | null;
};

/** Academic context relation shipped with audience-aware course options. */
export type CourseContextRelation = { id: number; name: string } | null;

/**
 * A course returned by the audience-aware course-options endpoint. Only courses
 * with at least one matching recipient that can access them are returned.
 */
export type CourseOption = NamedOption & {
  title: string;
  students_count: number;
  stage?: CourseContextRelation;
  grade?: CourseContextRelation;
};

/** Audience selection used to scope the course-options request. */
export type CourseOptionsParams = {
  audience_type: NotificationAudienceType;
  filters?: NotificationFilters;
  selected_student_ids?: number[];
};

export type CreateNotificationPayload = {
  title: string;
  body: string;
  audience_type: NotificationAudienceType;
  selected_student_ids?: number[];
  selected_instructor_ids?: number[];
  filters?: NotificationFilters;
  target_type?: NotificationTargetType;
  target_data?: {
    course_id?: number | null;
    announcement_id?: number | null;
    bundle_id?: number | null;
    url?: string | null;
  };
  send_now?: boolean;
  scheduled_at?: string | null;
};

export type SendResult = {
  notification: AdminNotification;
  recipients_count: number;
  sent_count: number;
  failed_count: number;
};

export const notificationService = {
  async list(params?: Query) {
    const response = await api.get<ApiEnvelope<PaginatedResult<AdminNotification>>>(
      "/mcp/notifications",
      { params, meta: { toastSuccess: false } }
    );
    return unwrapList<AdminNotification>(response.data);
  },
  async get(id: number | string) {
    const response = await api.get<ApiEnvelope<{ data: AdminNotification }>>(
      `/mcp/notifications/${id}`,
      { meta: { toastSuccess: false } }
    );
    return unwrapData<AdminNotification>(response.data);
  },
  async create(data: CreateNotificationPayload) {
    const response = await api.post<ApiEnvelope<{ data: SendResult }>>(
      "/mcp/notifications",
      data
    );
    return unwrapData<SendResult>(response.data);
  },
  async send(id: number | string) {
    const response = await api.post<ApiEnvelope<{ data: SendResult }>>(
      `/mcp/notifications/${id}/send`,
      {}
    );
    return unwrapData<SendResult>(response.data);
  },
  async delete(id: number | string) {
    const response = await api.delete(`/mcp/notifications/${id}`);
    return response.data;
  },
  async deleteAll() {
    const response = await api.delete<ApiEnvelope<{ data: { deleted: number } }>>(
      "/mcp/notifications"
    );
    return unwrapData<{ deleted: number }>(response.data) ?? { deleted: 0 };
  },
  async audienceOptions() {
    const response = await api.get<ApiEnvelope<{ data: AudienceOptions }>>(
      "/mcp/notifications/audience-options",
      { meta: { toastSuccess: false } }
    );
    return normalizeAudienceOptions(unwrapData<AudienceOptions>(response.data));
  },
  async courseOptions(params: CourseOptionsParams) {
    const response = await api.get<ApiEnvelope<{ data: CourseOption[] }>>(
      "/mcp/notifications/course-options",
      { params, meta: { toastSuccess: false } }
    );
    return unwrapData<CourseOption[]>(response.data) ?? [];
  },
  async previewAudience(data: Partial<CreateNotificationPayload>) {
    const response = await api.post<ApiEnvelope<{ data: { recipients_count: number } }>>(
      "/mcp/notifications/preview-audience",
      data,
      { meta: { toastSuccess: false } }
    );
    return unwrapData<{ recipients_count: number }>(response.data) ?? { recipients_count: 0 };
  },
};
